import { FC, useEffect, useState } from 'react'
import './Onboarding.scss'
import React from 'react'
import { Accordion, Badge, Button } from 'react-bootstrap'

type OnboardingQuestion = {
  questionId: string;
  title: string;
  options: string[];
  subTitle: string;
  answer?: string | string[] | null;
  video?: string;
  collapse?: [{ title: string; subTitle: string, badge: string, items: string[] }];
}

type VideoCollapseProps = {
  data: OnboardingQuestion;
  setIsDisable: (value: boolean) => void;
}

const VideoCollapse: FC<VideoCollapseProps> = ({ data, setIsDisable }) => {
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const handleToggle = (key: string) => {
    setActiveKey(activeKey === key ? null : key)
  }

  useEffect(() => {
    setIsDisable(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.answer]);

  const integrationTools = ['Hubspot', 'Salesforce', 'Stripe', 'Zapier', 'Make.com'];

  return (
    <div className='w-100'>
      {/* Top Center Section - Title and Subtitle */}
      <div className='text-center mb-8'>
        <h1 className='display-6 lh-base mb-2 fw-boldest onboarding-first-title'>
          Meet the Cellcast {' '}
          <span className='display-6 text-primary lh-base mb-2 fw-boldest'>
            Platform
          </span>
        </h1>
        <p className='onboarding-description'>
          Three powerful pillars working together to transform your customer journey
        </p>
      </div>

      {/* Main Content - Video Left, Collapse Right */}
      <div className='row g-6 align-items-start mb-8'>
        {/* Left Section - Video */}
        <div className='col-lg-6 col-md-12'>
          <div className='d-flex flex-column align-items-center'>
            <div className="videoouter">
              <div className="video_canvas">
                <iframe
                  width="455"
                  height="300"
                  id="video_canvas"
                  src={data.video ?? "https://www.youtube.com/embed/EUfO8Fg1gS8"}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                  title="Onboarding Video"
                  style={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}
                />
              </div>
            </div>

            {/* Integrations Section Below Video */}
            <div className='mt-6 w-100'>
              <div className='text-center mb-4'>
                <Badge
                  className="px-3 py-2 fs-6 rounded-pill bg-white onboarding-description shadow border border-secondary d-inline-flex align-items-center gap-2 fw-normal"
                >
                  <span
                    className="d-inline-block rounded-circle bg-success"
                    style={{ width: '6px', height: '6px' }}
                  ></span>
                  Integrates with 50+ tools
                </Badge>
              </div>

              {/* Integration Tool Tags and Next Button */}
              <div className='d-flex align-items-center justify-content-center gap-4 mt-4 flex-wrap'>
                <div className='d-flex flex-wrap justify-content-center gap-3'>
                  {integrationTools.map((tool, index) => (
                    <Badge
                      key={index}
                      className="px-3 py-2 fs-7 rounded-pill bg-white onboarding-description border border-secondary fw-normal"
                    >
                      {tool}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Section - Collapse Items */}
        <div className='col-lg-6 col-md-12'>
          <Accordion activeKey={activeKey} onSelect={(key) => setActiveKey(key as string)}>
            {data.collapse?.map((item, index) => {
              const isActive = activeKey === index.toString();
              return (
                <Accordion.Item
                  eventKey={index.toString()}
                  key={index}
                  className={`mb-4 border rounded transition-all duration-300 color-${index}`}
                >
                  <Accordion.Header
                    as="div"
                    className="d-flex justify-content-between align-items-start w-100"
                  >
                    <div className="d-flex flex-column w-100 pe-3" style={{ paddingLeft: '10px', marginLeft: '0' }}>
                      <div className="d-flex justify-content-between align-items-start w-100 mb-2">
                        <h5 className="option-label mb-0">{item.title}</h5>
                        <Button
                          variant={isActive ? 'primary' : 'outline-secondary'}
                          size="sm"
                          className={`ms-2 border fw-normal fs-7 bg-white ${isActive ? '' : 'text-muted'}`}
                          style={{ padding: '2px 6px', borderRadius: '8px', flexShrink: 0 }}
                          onClick={() => handleToggle(index.toString())}
                        >
                          {isActive ? 'Less' : 'More'}
                        </Button>
                      </div>
                      <p className="collapse-subtitle mb-0">{item.subTitle}</p>
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <ul className="mb-0" style={{ paddingLeft: '20px', marginLeft: '0' }}>
                      {item.items.map((listItem, idx) => (
                        <li key={idx} className="mb-2 fs-6 form-check-label">
                          {listItem}
                        </li>
                      ))}
                    </ul>
                    <Badge bg="primary" className="mt-4">
                      {item.badge}
                    </Badge>
                  </Accordion.Body>
                </Accordion.Item>
              )
            })}
          </Accordion>
        </div>
      </div>
    </div>
  );
};

export default React.memo(VideoCollapse)
